import { ReactNode, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { ArrowDown, ArrowUp, ArrowUpDown, Columns3 } from "lucide-react";

export interface TableColumn<T> {
  key: string;
  header: string;
  /** Se omesso, prova a leggere `row[key]` direttamente. */
  render?: (row: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  /**
   * Come si ordina questa colonna. Assente = colonna non ordinabile, e
   * l'intestazione resta inerte anche con `ordinabile` attivo.
   *
   * Va restituito il valore su cui ordinare, NON quello che si vede: `render`
   * produce spesso un nodo React (badge, link, due righe di testo) e ordinare su
   * quello non avrebbe senso. `null` significa "questa riga non ha il dato", e
   * finisce sempre in fondo — vedi la nota nell'implementazione.
   */
  sortValue?: (row: T) => string | number | null;
  /**
   * La colonna esiste ma parte spenta nel menu "Colonne". Serve alle colonne di
   * dettaglio che allargherebbero la tabella senza servire a tutti.
   * Ignorata se la tabella non abilita `sceltaColonne`.
   */
  nascostaDiDefault?: boolean;
  /**
   * La colonna non si puo' spegnere e non compare nemmeno nel menu: e' quella
   * che identifica la riga (il nome, la matricola) o l'unica azione disponibile.
   *
   * Serve anche alle colonne-azione con `header: ""`, che nel menu
   * comparirebbero come una casella senza etichetta — impossibile da capire e
   * quindi da riaccendere una volta spenta.
   *
   * Incompatibile con `nascostaDiDefault`: una colonna non puo' essere insieme
   * obbligatoria e spenta. Vince `sempreVisibile`.
   */
  sempreVisibile?: boolean;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  rowKey: (row: T) => string;
  isLoading?: boolean;
  /** Stringa o nodo: gli stati vuoti degli operatori sono su due righe. */
  emptyMessage?: ReactNode;
  onRowClick?: (row: T) => void;
  /**
   * Abilita l'ordinamento cliccando le intestazioni che dichiarano `sortValue`.
   * Opzionale, e spento di default: le tabelle che non lo chiedono si comportano
   * esattamente come prima.
   */
  ordinabile?: boolean;
  /**
   * Abilita il menu "Colonne". Il valore è l'ID con cui le preferenze vengono
   * salvate: due tabelle diverse con lo stesso ID si sovrascriverebbero a
   * vicenda, quindi va scelto per pagina (es. `"devices"`).
   */
  sceltaColonne?: string;
  /**
   * Ordinamento controllato dal chiamante, per tenerlo nell'URL (vedi
   * `useOrdinamentoUrl`): con `onOrdineChange` la tabella non tiene più un
   * ordine suo e mostra `ordine`. Senza, si comporta come prima.
   */
  ordine?: { key: string; verso: "asc" | "desc" } | null;
  onOrdineChange?: (ordine: { key: string; verso: "asc" | "desc" } | null) => void;
}

/** Prefisso delle chiavi in localStorage, per non collidere con altro. */
const CHIAVE_COLONNE = "winkonsupport_colonne_";

/**
 * Le colonne nascoste salvate per questa tabella.
 *
 * Lettura TOLLERANTE di proposito: se il valore salvato è illeggibile (JSON
 * rotto, forma inattesa) o cita colonne che nel frattempo sono state rimosse dal
 * codice, si riparte dai default invece di far esplodere la pagina. Una
 * preferenza di visualizzazione non vale il rischio di una schermata bianca, e
 * il salvataggio sopravvive a deploy che cambiano le colonne.
 */
function leggiNascoste(id: string, chiaviValide: Set<string>): Set<string> | null {
  try {
    const grezzo = localStorage.getItem(CHIAVE_COLONNE + id);
    if (!grezzo) return null;
    const parsed: unknown = JSON.parse(grezzo);
    if (!Array.isArray(parsed)) return null;
    return new Set(parsed.filter((k): k is string => typeof k === "string" && chiaviValide.has(k)));
  } catch {
    return null;
  }
}

export function Table<T>({
  columns,
  data,
  rowKey,
  isLoading = false,
  emptyMessage = "Nessun risultato.",
  onRowClick,
  ordinabile = false,
  sceltaColonne,
  ordine: ordineControllato,
  onOrdineChange,
}: TableProps<T>) {
  // --- Colonne visibili -----------------------------------------------------
  // L'inizializzatore gira una volta sola, al primo render: le preferenze si
  // leggono lì e non a ogni cambio di `columns`, altrimenti una colonna appena
  // mostrata dall'utente tornerebbe nascosta al primo re-render del chiamante.
  const [nascoste, setNascoste] = useState<Set<string>>(() => {
    const spegnibili = new Set(columns.filter((c) => !c.sempreVisibile).map((c) => c.key));
    const salvate = sceltaColonne ? leggiNascoste(sceltaColonne, spegnibili) : null;
    // Anche da una preferenza salvata: se una colonna e' diventata obbligatoria
    // dopo che l'utente l'aveva nascosta, torna visibile invece di restare
    // spenta per sempre senza piu' un modo per riaccenderla.
    if (salvate) return salvate;
    return new Set(columns.filter((c) => c.nascostaDiDefault && !c.sempreVisibile).map((c) => c.key));
  });

  useEffect(() => {
    if (!sceltaColonne) return;
    try {
      localStorage.setItem(CHIAVE_COLONNE + sceltaColonne, JSON.stringify([...nascoste]));
    } catch {
      // Quota piena o storage negato (navigazione privata): la tabella continua
      // a funzionare, si perde solo la memoria della scelta.
    }
  }, [nascoste, sceltaColonne]);

  const colonneVisibili = useMemo(
    () => (sceltaColonne ? columns.filter((c) => !nascoste.has(c.key)) : columns),
    [columns, nascoste, sceltaColonne]
  );

  // --- Ordinamento ----------------------------------------------------------
  //
  // ORDINA IN MEMORIA, cioè SOLO CIÒ CHE È STATO CARICATO. Oggi non è un limite
  // perché nessuna tabella dell'app è paginata: il backend restituisce tutte le
  // righe (nessun `take`/`skip` in `GET /api/devices`) e il browser le ha tutte,
  // quindi "ordina per data" ordina davvero l'intero elenco.
  //
  // IL GIORNO IN CUI SI INTRODUCE LA PAGINAZIONE QUESTO DIVENTA SBAGLIATO, e in
  // modo silenzioso: l'utente cliccherebbe "ordina per data" ottenendo l'ordine
  // delle sole righe della pagina corrente, e cercando "i device installati per
  // primi" vedrebbe i più vecchi *di quella pagina* credendoli i più vecchi in
  // assoluto. Un ordinamento che mente è peggio di un ordinamento che non c'è.
  //
  // A quel punto l'ordinamento va spostato lato server (`?order_by=&verso=`,
  // applicato nell'`orderBy` di Prisma) e questa parte va rimossa, non adattata:
  // tenerne due che si contendono le stesse righe è la ricetta per un elenco che
  // cambia ordine a seconda di quale ha vinto.
  const [ordineInterno, setOrdineInterno] = useState<{ key: string; verso: "asc" | "desc" } | null>(null);
  const ordine = onOrdineChange ? ordineControllato ?? null : ordineInterno;

  const dataOrdinata = useMemo(() => {
    if (!ordinabile || !ordine) return data;
    const col = columns.find((c) => c.key === ordine.key);
    if (!col?.sortValue) return data;
    const leggi = col.sortValue;
    const segno = ordine.verso === "asc" ? 1 : -1;

    // Copia prima di ordinare: `sort` muta l'array, e quello arriva dal
    // chiamante — riordinarlo sul posto gli cambierebbe lo stato sotto i piedi.
    return [...data].sort((a, b) => {
      const va = leggi(a);
      const vb = leggi(b);

      // I VUOTI SEMPRE IN FONDO, in entrambe le direzioni. Non è una preferenza
      // estetica: `null` convertito con `Number()` vale 0, quindi una riga senza
      // dato si ordinerebbe come uno zero vero, in mezzo ai valori reali. Il
      // confronto va fatto PRIMA di qualunque conversione.
      const aVuoto = va === null || va === undefined || va === "";
      const bVuoto = vb === null || vb === undefined || vb === "";
      if (aVuoto && bVuoto) return 0;
      if (aVuoto) return 1;
      if (bVuoto) return -1;

      if (typeof va === "number" && typeof vb === "number") return (va - vb) * segno;

      // `numeric` perché le matricole sono alfanumeriche: senza, "UB1200010"
      // verrebbe prima di "UB120009". Locale italiana per accenti e maiuscole.
      return String(va).localeCompare(String(vb), "it", { numeric: true, sensitivity: "base" }) * segno;
    });
  }, [data, ordine, columns, ordinabile]);

  /** Primo click: crescente. Click successivi: inverte. */
  const alternaOrdine = (key: string) => {
    const next: { key: string; verso: "asc" | "desc" } =
      ordine?.key === key ? { key, verso: ordine.verso === "asc" ? "desc" : "asc" } : { key, verso: "asc" };
    if (onOrdineChange) onOrdineChange(next);
    else setOrdineInterno(next);
  };

  return (
    <div>
      {sceltaColonne && (
        <div className="mb-2 flex justify-end">
          {/* `details` invece di un menu con stato: apertura, chiusura e
              chiusura con Esc arrivano dal browser, senza gestori di click
              fuori dall'elemento che sono la parte che si rompe. */}
          <details className="relative">
            <summary className="inline-flex cursor-pointer list-none items-center gap-1.5 rounded-lg border border-line bg-white px-3 py-1.5 text-xs font-semibold text-ink/70 shadow-card transition-colors hover:text-ink [&::-webkit-details-marker]:hidden">
              <Columns3 className="h-3.5 w-3.5" aria-hidden="true" />
              Colonne
              {nascoste.size > 0 && <span className="text-ink/40">({colonneVisibili.length}/{columns.length})</span>}
            </summary>
            <div className="absolute right-0 z-20 mt-1 max-h-80 w-60 overflow-y-auto rounded-card border border-line bg-white p-2 shadow-lift">
              {columns.filter((c) => !c.sempreVisibile).map((col) => {
                const visibile = !nascoste.has(col.key);
                // L'ultima colonna visibile non si può togliere: una tabella
                // senza colonne è una griglia vuota da cui non si torna
                // indietro se non riaprendo il menu al buio.
                const ultima = visibile && colonneVisibili.filter((c) => !c.sempreVisibile).length === 1;
                return (
                  <label
                    key={col.key}
                    className={clsx(
                      "flex items-center gap-2 rounded px-2 py-1.5 text-sm",
                      ultima ? "cursor-not-allowed text-ink/35" : "cursor-pointer text-ink hover:bg-primary/5"
                    )}
                    title={ultima ? "Almeno una colonna deve restare visibile." : undefined}
                  >
                    <input
                      type="checkbox"
                      checked={visibile}
                      disabled={ultima}
                      onChange={() =>
                        setNascoste((prec) => {
                          const next = new Set(prec);
                          if (next.has(col.key)) next.delete(col.key);
                          else next.add(col.key);
                          return next;
                        })
                      }
                    />
                    {col.header}
                  </label>
                );
              })}
            </div>
          </details>
        </div>
      )}

      <div className="overflow-x-auto rounded-card border border-line bg-white shadow-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-primary text-left text-xs font-semibold uppercase tracking-wide text-white">
              {colonneVisibili.map((col) => {
                const cliccabile = ordinabile && !!col.sortValue;
                const attiva = ordine?.key === col.key;
                return (
                  <th
                    key={col.key}
                    className={clsx("px-4 py-3", col.headerClassName)}
                    // `aria-sort` è il modo standard di dire a uno screen reader
                    // che questa colonna è quella che ordina, e in che verso.
                    aria-sort={attiva ? (ordine!.verso === "asc" ? "ascending" : "descending") : undefined}
                  >
                    {cliccabile ? (
                      <button
                        type="button"
                        onClick={() => alternaOrdine(col.key)}
                        className="inline-flex items-center gap-1 uppercase tracking-wide hover:underline"
                      >
                        {col.header}
                        {attiva ? (
                          ordine!.verso === "asc" ? (
                            <ArrowUp className="h-3 w-3" aria-hidden="true" />
                          ) : (
                            <ArrowDown className="h-3 w-3" aria-hidden="true" />
                          )
                        ) : (
                          // Sempre presente ma smorzata: senza, le intestazioni
                          // cliccabili si distinguono da quelle inerti solo
                          // provandoci.
                          <ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="border-b border-line">
                  {colonneVisibili.map((col) => (
                    <td key={col.key} className="px-4 py-3">
                      <div className="h-4 w-3/4 animate-pulse rounded bg-black/5" />
                    </td>
                  ))}
                </tr>
              ))}

            {!isLoading && data.length === 0 && (
              <tr>
                <td colSpan={colonneVisibili.length} className="px-4 py-10 text-center text-sm text-ink/50">
                  {emptyMessage}
                </td>
              </tr>
            )}

            {!isLoading &&
              dataOrdinata.map((row) => (
                <tr
                  key={rowKey(row)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={clsx(
                    "border-b border-line last:border-0 transition-colors duration-150",
                    onRowClick && "cursor-pointer hover:bg-primary/5"
                  )}
                >
                  {colonneVisibili.map((col) => (
                    <td key={col.key} className={clsx("px-4 py-3 align-middle text-ink", col.className)}>
                      {col.render ? col.render(row) : ((row as Record<string, unknown>)[col.key] as ReactNode)}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
